import React, { useEffect, useRef, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData, useRouteError } from "react-router";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import ClientOnly from "~/components/shared-components/ClentOnly";
import DisplayBookCard from "~/components/shared-components/DisplayBookCard";
import LoadingSpinner from "~/components/shared-components/LoadingSpinner";
import NodataFound from "~/components/shared-components/NoDataFound";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { SatsangSevak, SatsangSevakGroup, satsangSevakRows } from "~/interface/satsng-sevak-types/satsang-sevak";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { getSatsangSevakGroupData } from "~/lib/helperFuncation/Satsang-sevak/getSatsangSevakGroupData";
import { cn } from "~/lib/utils";
import API_PATH from "~/utils/api-routes";
import { BLUR_HASH, CACHED_PAGE, FALLBACK_IMAGE_URL, LANG, shortMonthName } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import api from "~/utils/Interceptor";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("satsangSevak", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const selectedYear = params.year || 0;
  const lang = params.lang;
  const apiService = new ApiService(lang || LANG.english);

  // Fetch available years for filtering (always fetch year data)
  const years = await api.get(`${API_PATH.SATSANG_SEVAK_YEAR}`);
  const yearArray = years.data.data;

  // Fetch data for the selected year (if response is invalid, return empty data)
  const response = await apiService.getSatsangSevakData({
    pageIndex: 1,
    pageSize: 50,
  });

  //function for do group ssatsang sevak data based on year
  const getSatsangSevakGroupData = (satsangSevakData: satsangSevakRows[]): SatsangSevakGroup => {
    const grouped: SatsangSevakGroup = satsangSevakData.reduce((acc, data) => {
      if (!acc[data.year]) {
        acc[data.year] = [];
      }
      acc[data.year].push(data);
      return acc;
    }, {} as SatsangSevakGroup);

    // //Sort the year in descending order
    const sortedYears = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

    //create a new object with sorted year keys as number
    const sortedGrouped: SatsangSevakGroup = {};
    for (const year of sortedYears) {
      sortedGrouped[Number(year)] = grouped[Number(year)];
    }
    return sortedGrouped;
  };

  const rows = response ? getSatsangSevakGroupData(response.rows) : [];
  const availableYears = Object.keys(rows)
    .map((year) => Number(year))
    .sort((a, b) => (a < b ? 1 : -1));

  const DATA = {
    rows,
    years: yearArray, // Always return the years data
    selectedYear: selectedYear,
    availableYears,
    title: lang === LANG.english ? "Satsang Sevak" : "સત્સંગ સેવક",
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.SATSANG_SEVAK.HEADERS, Etag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "Satsang Sevak", path: "/satsang-sevak" },
  ],
};

const SatsangSevek = () => {
  const { showTopButton } = useDisplayStickyButton();
  const { t } = useTranslation();

  const { rows, years, title, availableYears } = useLoaderData<SatsangSevak>();
  const virtuoso = useRef<VirtuosoHandle | null>(null);
  const yearRefs = useRef<Record<number, HTMLElement | null>>({});
  const ticking = useRef(false);
  const lang = useLanguageParams();
  const apiService = new ApiService(lang || LANG.english);

  const [satsangSevak, setSatsangSevak] = useState<SatsangSevakGroup>(rows);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [availableYearsArray, setavailableYearsArray] = useState<number[]>(availableYears);
  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const handleFetchNextPageData = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const response = await apiService.getSatsangSevakData({
        pageIndex: pageIndex + 1,
        pageSize: 50,
      });
      // Check if the response from SATSANG_SEVAK_LIST is valid
      if (response.rows && response.rows.length > 0) {
        const groupedData = getSatsangSevakGroupData(response.rows);
        setSatsangSevak((prev) => ({
          ...prev,
          ...Object.keys(groupedData).reduce((acc, year) => {
            acc[year] = [...(prev[year] || []), ...groupedData[year]];
            return acc;
          }, {} as SatsangSevakGroup),
        }));
        const newAvailableYears = Object.keys(groupedData)
          .map((year) => Number(year))
          .sort((a, b) => (a < b ? 1 : -1));
        setavailableYearsArray((prevYears) => {
          const uniqueYears = Array.from(new Set([...prevYears, ...newAvailableYears]));
          return uniqueYears.sort((a, b) => (a < b ? 1 : -1));
        });
        setPageIndex((prevIndex) => prevIndex + 1);
        setLoading(false);
      } else {
        setSatsangSevak({});
        setLoading(false);
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false);
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (ticking.current) return;
    ticking.current = true;

    window.requestAnimationFrame(() => {
      let closestYear = selectedYear;
      let closestOffset = Infinity;

      Object.entries(yearRefs.current).forEach(([yearStr, el]) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const offset = Math.abs(rect.top);
          if (offset < closestOffset) {
            closestOffset = offset;
            closestYear = parseInt(yearStr);
          }
        }
      });

      if (closestYear !== selectedYear) {
        setSelectedYear(closestYear);
      }

      ticking.current = false;
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedYear]);

  const handleOnChipClick = async (targetYear: number) => {
    if (availableYearsArray.includes(targetYear)) {
      scrollToYear(targetYear);
      return;
    }

    setLoading(true);
    let found = false;
    let tempPageIndex = pageIndex + 1;
    const newYears: number[] = [];

    while (!found && hasMore) {
      try {
        const response = await api.get(`${API_PATH.SATSANG_SEVAK_LIST}`, {
          params: {
            pageIndex: tempPageIndex,
            pageSize: 50,
            lang: LANG.gujarati ? "gu" : "en",
          },
        });

        const newRows = response.data.data.rows;
        if (!newRows || newRows.length === 0) {
          setHasMore(false);
          break;
        }

        const groupedData = getSatsangSevakGroupData(newRows);
        setSatsangSevak((prev) => ({
          ...prev,
          ...Object.keys(groupedData).reduce((acc, year) => {
            acc[year] = [...(prev[year] || []), ...groupedData[year]];
            return acc;
          }, {} as SatsangSevakGroup),
        }));
        const fetchedYears = Object.keys(groupedData).map(Number);
        newYears.push(...fetchedYears);

        const allYears = Array.from(new Set([...availableYearsArray, ...newYears]));
        setavailableYearsArray(allYears.sort((a, b) => b - a));

        if (fetchedYears.includes(targetYear)) {
          found = true;
        }

        tempPageIndex += 1;
      } catch (error) {
        break;
      }
    }

    if (found) {
      scrollToYear(targetYear);
      setPageIndex(tempPageIndex - 1);
    }

    setLoading(false);
  };

  const scrollToYear = (year: number) => {
    const index = years.indexOf(year);
    if (virtuoso.current && index !== -1) {
      virtuoso.current.scrollToIndex({
        index,
        behavior: "smooth",
        offset: -150,
      });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="w-full flex flex-col gap-2 pb-10" style={{ minHeight: "100vh" }}>
      {/* {showTopButton ? ( */}
      <div className={cn(showTopButton ? "filter-chip-animation" : "filter-chip-non-animation")}>
        {/* <FilterChip /> */}
        <Chip
          chipData={years}
          selectedValue={selectedYear ? Number(selectedYear) : years[0]}
          onClick={(year) => {
            handleOnChipClick(Number(year));
          }}
        />
      </div>
      {/* ) : (
        <div className="filter-chip-non-animation ">
          <FilterChip />
        </div>
      )} */}

      <ClientOnly
        fallback={availableYearsArray.map((y) => (
          <React.Fragment key={y}>
            <SatsangSevakCard satsangSevak={satsangSevak} year={y} />
          </React.Fragment>
        ))}
      >
        <Virtuoso
          ref={virtuoso}
          style={{ height: "100%", width: "100%" }}
          // style={{
          //   height: "100vh", // ← EXPLICIT HEIGHT NEEDED when useWindowScroll={false}
          //   width: "100%",
          // }}
          data={availableYearsArray}
          endReached={handleFetchNextPageData}
          increaseViewportBy={200}
          useWindowScroll
          // useWindowScroll={false} // ← Set t
          itemContent={(i, date) => {
            return (
              <div ref={(el) => (yearRefs.current[date] = el)} className="max-md:px-2">
                <SatsangSevakCard satsangSevak={satsangSevak} year={date} />
              </div>
            );
          }}
          components={{
            Footer: () =>
              loading && hasMore ? (
                <LoadingSpinner />
              ) : (
                <div className="w-full flex justify-center items-center mt-5">
                  <p className="font-abcArizonaFlareVariable font-medium text-[14px] text-textColorPrimary">{t("common.seeAllData")}</p>
                </div>
              ),
          }}
        />
      </ClientOnly>
    </div>
  );
};

const SatsangSevakCard = ({ satsangSevak, year }: { satsangSevak: SatsangSevakGroup; year: number }) => {
  return (
    <section className="w-full mx-2 px-3 pb-3 relative" id={year.toString()}>
      <div className="h-full w-[3px] absolute left-0 top-6 bg-eventcardGradient opacity-100" />
      <div className="size-[10px] rounded-full absolute top-4 left-[-3px] bg-textColor" />
      <p className="font-abcArizonaFlareVariable font-medium text-textColor text-[25px]" title={`Satsang Sevak - ${year}`}>
        {`${year}`}
      </p>
      <div className="w-full flex flex-col justify-start items-start gap-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-4 lg:gap-6 px-3 h-full w-full">
          {Array.isArray(satsangSevak[year]) &&
            satsangSevak[year]?.map((book) => {
              return (
                <React.Fragment key={book.id}>
                  <MemoizedSatsangSevakComponent book={book} />
                </React.Fragment>
              );
            })}
        </div>
      </div>
    </section>
  );
};

const MemoizedSatsangSevakComponent = React.memo(({ book }: { book: satsangSevakRows }) => {
  const lang = useLanguageParams() as "en" | "gu";
  const monthName = shortMonthName[book.month - 1];
  return (
    <DisplayBookCard
      blur_hash={book.img ? book.img.bh : BLUR_HASH}
      name={`${monthName[lang]} ${book.year}`}
      navigatePath={book.pdf_path}
      url={
        book.img
          ? {
              small: book.img.s.url,
              medium: book.img.m.url,
              large: book.img.l.url,
              hd: book.img.hd.url,
              original: book.img.og.url,
            }
          : {
              small: FALLBACK_IMAGE_URL,
              medium: FALLBACK_IMAGE_URL,
              large: FALLBACK_IMAGE_URL,
              hd: FALLBACK_IMAGE_URL,
              original: FALLBACK_IMAGE_URL,
            }
      }
      pdf_path={book.pdf_path}
      img_aspect={book.img_aspect}
      downLoadFineName={`${monthName.en}_${book.year}_Satsang-Sevak`}
    />
  );
});

export const ErrorBoundary = () => {
  const error = useRouteError();

  return (
    <div className="w-full h-screen ">
      <NodataFound text="No data found for Satsang Sevak" />;
    </div>
  );
};

export default SatsangSevek;
